import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ordersAPI, pricingAPI, walletAPI } from '../../services/api';
import { 
  PlusIcon, 
  XMarkIcon, 
  MapPinIcon,
  TruckIcon,
  ArrowDownIcon,
  PhotoIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  CurrencyDollarIcon,
  MapIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import LocationPickerSimple from '../Maps/LocationPickerSimple';
import { GoogleMap, useJsApiLoader, Marker as GoogleMarker, Polyline as GooglePolyline, InfoWindow } from '@react-google-maps/api';

interface LocationData {
  address: string;
  lat: number;
  lng: number;
}

interface Stop {
  id: string;
  type: 'pickup' | 'dropoff';
  location: LocationData | null;
  contactName: string;
  contactPhone: string;
  contactEmail: string;
  instructions?: string;
  photoUrl?: string;
  timeWindow?: string;
  specialHandling?: string[];
  stopType?: string;
}

interface StopCardProps {
  stop: Stop;
  index: number;
  onUpdate: (id: string, field: string, value: any) => void;
  onRemove: (id: string) => void;
  canRemove: boolean;
}

interface PricingBreakdown {
  baseFare: number;
  distanceFee: number;
  weightFee: number;
  stopsFee: number;
  total: number;
  distance?: number;
  durationFee?: number;
  vatAmount?: number;
  managementFee?: number;
  paystackFee?: number;
  subtotal?: number;
  totalDuration?: number;
  segments?: Array<{
    from_address: string;
    to_address: string;
    distance_km: number;
    duration_minutes: number;
    segment_cost: number;
  }>;
}

const StopCard: React.FC<StopCardProps> = ({ stop, index, onUpdate, onRemove, canRemove }) => {
  const isPickup = stop.type === 'pickup';
  const [phoneValid, setPhoneValid] = useState<boolean | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validatePhone = (phone: string) => {
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 0) {
      setPhoneValid(null);
      return;
    }
    // Accept 10-11 digits (local) or 13 digits (with +234)
    setPhoneValid(
      (cleaned.length >= 10 && cleaned.length <= 11) || 
      cleaned.length === 13
    );
  };

  const formatPhone = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 3) return cleaned;
    if (cleaned.length <= 6) return `${cleaned.slice(0, 3)} ${cleaned.slice(3)}`;
    if (cleaned.length <= 9) return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6)}`;
    if (cleaned.length <= 11) return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6, 11)}`;
    // Handle 12-13 digits (international format with country code)
    if (cleaned.length <= 13) return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6, 10)} ${cleaned.slice(10, 13)}`;
    return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6, 10)} ${cleaned.slice(10, 13)}`;
  };

  const handlePhoneChange = (phone: string) => {
    const formatted = formatPhone(phone);
    onUpdate(stop.id, 'contactPhone', formatted);
    validatePhone(phone);
  };

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        onUpdate(stop.id, 'photoUrl', reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const specialHandlingOptions = ['Fragile', 'Heavy', 'Perishable', 'Valuable', 'Urgent'];

  return (
    <div
      className={`relative bg-white rounded-volexx-lg border-2 ${
        isPickup ? 'border-volexx-blue-300' : 'border-orange-300'
      } p-6 mb-4 transition-all duration-300 hover:shadow-volexx-lg animate-fadeInUp`}
    >
      <div
        className="absolute top-4 left-4 w-10 h-10 rounded-full bg-gradient-to-br from-white to-gray-100 flex items-center justify-center shadow-md border-2 border-gray-200"
      >
        <span className="text-lg font-bold text-gray-700">{index + 1}</span>
      </div>

      <div className="flex items-center justify-between mb-4 ml-14">
        <div className="flex items-center space-x-3">
          <div
            className={`p-2.5 rounded-volexx shadow-sm ${
              isPickup ? 'bg-gradient-blue' : 'bg-gradient-orange'
            }`}
          >
            {isPickup ? (
              <MapPinIcon className="h-6 w-6 text-white" />
            ) : (
              <TruckIcon className="h-6 w-6 text-white" />
            )}
          </div>
          <div>
            <h3 className={`font-bold text-lg ${isPickup ? 'text-volexx-blue-700' : 'text-orange-700'}`}>
              {isPickup ? 'Pickup' : 'Drop-off'} Location
            </h3>
            <p className="text-xs text-gray-500 flex items-center space-x-1">
              {stop.location && (
                <>
                  <CheckCircleIcon className="h-3 w-3 text-green-500" />
                  <span className="text-green-600 font-medium">Address verified</span>
                </>
              )}
              {!stop.location && <span>Awaiting address</span>}
            </p>
          </div>
        </div>
        {canRemove && (
          <button
            type="button"
            onClick={() => onRemove(stop.id)}
            className="p-2 text-red-600 hover:bg-red-50 rounded-volexx transition-all transform hover:scale-110"
            title="Remove stop"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        )}
      </div>

      <div className="space-y-4">
        <LocationPickerSimple
          label={`${isPickup ? 'Pickup' : 'Drop-off'} Address`}
          initialLocation={stop.location || undefined}
          onLocationSelect={(location) => onUpdate(stop.id, 'location', location)}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Contact Name *
            </label>
            <input
              type="text"
              className="form-input"
              placeholder="John Doe"
              value={stop.contactName}
              onChange={(e) => onUpdate(stop.id, 'contactName', e.target.value)}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center justify-between">
              <span>Contact Phone *</span>
              {phoneValid === true && (
                <span className="text-xs text-green-600 flex items-center">
                  <CheckCircleIcon className="h-3 w-3 mr-1" /> Valid
                </span>
              )}
              {phoneValid === false && (
                <span className="text-xs text-red-600 flex items-center">
                  <ExclamationTriangleIcon className="h-3 w-3 mr-1" /> 10-11 digits required
                </span>
              )}
            </label>
            <input
              type="tel"
              className={`form-input ${phoneValid === false ? 'border-red-300 focus:border-red-500' : ''} ${phoneValid === true ? 'border-green-300' : ''}`}
              placeholder="080 XXX XXXX"
              value={stop.contactPhone}
              onChange={(e) => handlePhoneChange(e.target.value)}
              required
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Contact Email *
          </label>
          <input
            type="email"
            className="form-input"
            placeholder="contact@example.com"
            value={stop.contactEmail}
            onChange={(e) => onUpdate(stop.id, 'contactEmail', e.target.value)}
            required
          />
          <p className="text-xs text-gray-500 mt-1">OTP will be sent to this email and phone number</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Package Photo (Optional)
          </label>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handlePhotoUpload}
          />
          <div className="flex items-center space-x-3">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-volexx transition-colors"
            >
              <PhotoIcon className="h-5 w-5 text-gray-600" />
              <span className="text-sm font-medium text-gray-700">
                {stop.photoUrl ? 'Change Photo' : 'Upload Photo'}
              </span>
            </button>
            {stop.photoUrl && (
              <img
                src={stop.photoUrl}
                alt="Package"
                className="h-16 w-16 object-cover rounded-volexx border-2 border-gray-200"
              />
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Special Instructions (Optional)
          </label>
          <textarea
            className="form-input"
            rows={2}
            placeholder={`Instructions for ${isPickup ? 'pickup' : 'delivery'}...`}
            value={stop.instructions || ''}
            onChange={(e) => onUpdate(stop.id, 'instructions', e.target.value)}
          />
        </div>
      </div>
    </div>
  );
};

const PricingPreview: React.FC<{ breakdown: PricingBreakdown; weight: string; pricingError?: string; showDetailed?: boolean }> = ({ breakdown, weight, pricingError, showDetailed = false }) => {
  return (
    <div className="sticky top-4 bg-gradient-to-br from-volexx-blue-500 to-volexx-blue-600 rounded-volexx-xl p-6 text-white shadow-volexx-xl animate-fadeInUp">
      <div className="text-center">
        <div className="flex items-center justify-center space-x-2 mb-2">
          <CurrencyDollarIcon className="h-6 w-6" />
          <h3 className="text-lg font-semibold opacity-90">Estimated Total</h3>
        </div>
        
        <div className="my-6">
          <div className="text-5xl font-bold">₦{breakdown.total.toLocaleString()}</div>
        </div>

        {pricingError ? (
          <div className="mt-4 bg-white/10 rounded-volexx p-3 text-xs">
            <p className="opacity-90">⚠️ {pricingError}</p>
          </div>
        ) : breakdown.distance && breakdown.distance > 0 ? (
          <div className="mt-4 space-y-3">
            <div className="bg-white/10 rounded-volexx p-3 text-sm">
              <div className="flex items-center justify-center space-x-4">
                <span className="opacity-90">📍 {breakdown.distance.toFixed(1)} km</span>
                {breakdown.totalDuration && (
                  <>
                    <span className="opacity-50">•</span>
                    <span className="opacity-90">⏱️ {breakdown.totalDuration.toFixed(0)} min</span>
                  </>
                )}
              </div>
            </div>
            
            {showDetailed && (
              <div className="bg-white/10 rounded-volexx p-4 text-left space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="opacity-80">Base Fare</span>
                  <span className="font-medium">₦{breakdown.baseFare.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="opacity-80">Distance Fee</span>
                  <span className="font-medium">₦{breakdown.distanceFee.toLocaleString()}</span>
                </div>
                {breakdown.durationFee && breakdown.durationFee > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="opacity-80">Duration Fee</span>
                    <span className="font-medium">₦{breakdown.durationFee.toLocaleString()}</span>
                  </div>
                )}
                {breakdown.weightFee > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="opacity-80">Weight Fee</span>
                    <span className="font-medium">₦{breakdown.weightFee.toLocaleString()}</span>
                  </div>
                )}
                {breakdown.vatAmount && breakdown.vatAmount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="opacity-80">VAT</span>
                    <span className="font-medium">₦{breakdown.vatAmount.toLocaleString()}</span>
                  </div>
                )}
                {breakdown.managementFee && breakdown.managementFee > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="opacity-80">Service Fee</span>
                    <span className="font-medium">₦{breakdown.managementFee.toLocaleString()}</span>
                  </div>
                )}
                <div className="border-t border-white/20 pt-2 mt-2 flex justify-between font-bold">
                  <span>Total</span>
                  <span>₦{breakdown.total.toLocaleString()}</span>
                </div>
              </div>
            )}
            
            <p className="text-xs opacity-75">Real-time pricing via Google Maps</p>
          </div>
        ) : (
          <p className="text-xs opacity-75 mt-4">Calculating pricing...</p>
        )}
      </div>
    </div>
  );
};

// Custom marker component for Google Maps with numbering
const CustomMarker: React.FC<{
  position: { lat: number; lng: number };
  stopNumber: number;
  isPickup: boolean;
  stop: Stop;
  onClick: () => void;
}> = ({ position, stopNumber, isPickup, stop, onClick }) => {
  const bgColor = isPickup ? '#0ea5e9' : '#f97316';
  const icon = isPickup ? '📍' : '📦';
  
  const svgMarker = {
    path: 'M 0,0 C -2,-20 -10,-22 -10,-30 A 10,10 0 1,1 10,-30 C 10,-22 2,-20 0,0 z',
    fillColor: bgColor,
    fillOpacity: 1,
    strokeWeight: 2,
    strokeColor: '#ffffff',
    scale: 1,
    labelOrigin: new google.maps.Point(0, -30)
  };

  return (
    <GoogleMarker
      position={position}
      icon={svgMarker}
      label={{
        text: `${icon}${stopNumber}`,
        color: '#ffffff',
        fontSize: '14px',
        fontWeight: 'bold'
      }}
      onClick={onClick}
    />
  );
};

const RouteMap: React.FC<{ stops: Stop[] }> = ({ stops }) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [selectedMarker, setSelectedMarker] = useState<Stop | null>(null);
  const [routePath, setRoutePath] = useState<google.maps.LatLng[]>([]);
  
  // Memoize validStops to prevent infinite re-renders
  const validStops = useMemo(() => stops.filter((s) => s.location), [stops]);

  // Load Google Maps using environment variable (key should be domain-restricted in Google Cloud Console)
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
    libraries: ['geometry', 'places']
  });

  const center = validStops.length > 0
    ? { lat: validStops[0].location!.lat, lng: validStops[0].location!.lng }
    : { lat: 6.5244, lng: 3.3792 }; // Lagos default

  // Auto-fit bounds when map loads or stops change
  const onLoad = useCallback((map: google.maps.Map) => {
    setMap(map);
  }, []);

  // Create a stable waypoints key to prevent infinite loops
  const waypointsKey = useMemo(() => 
    validStops.map(s => `${s.location!.lat},${s.location!.lng}`).join('|'),
    [validStops]
  );

  // Fetch road-following route from backend
  useEffect(() => {
    if (validStops.length < 2) {
      setRoutePath([]);
      return;
    }

    const fetchDirections = async () => {
      try {
        const waypoints = validStops.map(stop => ({
          lat: stop.location!.lat,
          lng: stop.location!.lng
        }));

        const response = await fetch('/api/directions', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ waypoints })
        });

        if (response.ok) {
          const data = await response.json();
          if (data.status === 'OK' && data.routes && data.routes.length > 0) {
            const route = data.routes[0];
            const encodedPolyline = route.overview_polyline?.points;
            
            if (encodedPolyline) {
              const decodedPath = google.maps.geometry.encoding.decodePath(encodedPolyline);
              setRoutePath(decodedPath);
            }
          }
        }
      } catch (error) {
        console.error('Failed to fetch directions:', error);
      }
    };

    fetchDirections();
  }, [waypointsKey]);

  useEffect(() => {
    if (map && validStops.length > 0) {
      const bounds = new google.maps.LatLngBounds();
      validStops.forEach(stop => {
        bounds.extend({ lat: stop.location!.lat, lng: stop.location!.lng });
      });
      map.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
      
      // Limit max zoom
      const listener = google.maps.event.addListenerOnce(map, 'bounds_changed', () => {
        if (map.getZoom()! > 15) {
          map.setZoom(15);
        }
      });
      return () => google.maps.event.removeListener(listener);
    }
  }, [map, validStops]);

  const handleRecenter = () => {
    if (map && validStops.length > 0) {
      const bounds = new google.maps.LatLngBounds();
      validStops.forEach(stop => {
        bounds.extend({ lat: stop.location!.lat, lng: stop.location!.lng });
      });
      map.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
    }
  };

  if (validStops.length === 0) {
    return (
      <div className="bg-white rounded-volexx-lg p-8 text-center border-2 border-dashed border-gray-300">
        <MapIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
        <p className="text-gray-500 font-medium">Add locations to see route preview</p>
        <p className="text-xs text-gray-400 mt-2">Your route will be visualized here</p>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="bg-white rounded-volexx-lg p-8 text-center border-2 border-gray-200">
        <div className="animate-pulse">
          <MapIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 font-medium">Loading map...</p>
        </div>
      </div>
    );
  }

  // Road-following route options (only render when we have actual route data)
  const polylineOptions = {
    path: routePath,
    strokeColor: '#0ea5e9',
    strokeOpacity: 0.8,
    strokeWeight: 4,
    geodesic: false
  };

  return (
    <div className={`relative ${isFullscreen ? 'fixed inset-0 z-50 bg-white' : 'rounded-volexx-lg overflow-hidden'} shadow-volexx-lg border-2 border-gray-200 animate-fadeInUp`}>
      <div className="bg-white px-4 py-3 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-volexx-blue-500 to-volexx-blue-600 flex items-center justify-center">
            <MapIcon className="h-4 w-4 text-white" />
          </div>
          <div>
            <h3 className="font-bold text-gray-900 text-sm">Route Preview</h3>
            <p className="text-xs text-gray-500">{validStops.length} stop{validStops.length > 1 ? 's' : ''}</p>
          </div>
        </div>
        <button
          onClick={() => setIsFullscreen(!isFullscreen)}
          className="p-2 hover:bg-gray-100 rounded-volexx transition-colors"
          title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
        >
          {isFullscreen ? (
            <XMarkIcon className="h-5 w-5 text-gray-600" />
          ) : (
            <svg className="h-5 w-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
            </svg>
          )}
        </button>
      </div>

      <GoogleMap
        mapContainerStyle={{ height: isFullscreen ? 'calc(100vh - 64px)' : '400px', width: '100%' }}
        center={center}
        zoom={13}
        onLoad={onLoad}
        options={{
          zoomControl: true,
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: false
        }}
      >
        {validStops.map((stop, index) => {
          const stopNumber = index + 1;
          const isPickup = stop.type === 'pickup';
          
          return (
            <CustomMarker
              key={stop.id}
              position={{ lat: stop.location!.lat, lng: stop.location!.lng }}
              stopNumber={stopNumber}
              isPickup={isPickup}
              stop={stop}
              onClick={() => setSelectedMarker(stop)}
            />
          );
        })}

        {selectedMarker && (
          <InfoWindow
            position={{ lat: selectedMarker.location!.lat, lng: selectedMarker.location!.lng }}
            onCloseClick={() => setSelectedMarker(null)}
          >
            <div className="text-sm p-2">
              <div className="flex items-center space-x-2 mb-2">
                <div className={`w-6 h-6 rounded-full ${selectedMarker.type === 'pickup' ? 'bg-volexx-blue-500' : 'bg-orange-500'} text-white flex items-center justify-center text-xs font-bold`}>
                  {validStops.indexOf(selectedMarker) + 1}
                </div>
                <strong className={selectedMarker.type === 'pickup' ? 'text-volexx-blue-600' : 'text-orange-600'}>
                  {selectedMarker.type === 'pickup' ? '📍 Pickup' : '📦 Drop-off'}
                </strong>
              </div>
              <p className="text-xs text-gray-700 mb-2">{selectedMarker.location!.address}</p>
              {selectedMarker.contactName && (
                <p className="text-xs text-gray-600">👤 {selectedMarker.contactName}</p>
              )}
              {selectedMarker.contactPhone && (
                <p className="text-xs text-gray-600">📞 {selectedMarker.contactPhone}</p>
              )}
            </div>
          </InfoWindow>
        )}

        {routePath.length > 0 && <GooglePolyline options={polylineOptions} />}

        {/* Recenter button */}
        <button
          onClick={handleRecenter}
          className="absolute bottom-24 right-4 bg-white p-3 rounded-volexx shadow-lg border border-gray-300 hover:bg-gray-50 transition-colors"
          title="Recenter route"
          aria-label="Recenter map to show all stops"
        >
          <svg className="h-5 w-5 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
          </svg>
        </button>
      </GoogleMap>
    </div>
  );
};

const OrderSummaryModal: React.FC<{
  stops: Stop[];
  formData: any;
  onConfirm: () => void;
  onClose: () => void;
}> = ({ stops, formData, onConfirm, onClose }) => {
  const pickups = stops.filter((s) => s.type === 'pickup');
  const dropoffs = stops.filter((s) => s.type === 'dropoff');

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 animate-fadeIn">
      <div className="bg-white rounded-volexx-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-scaleIn">
        <div className="sticky top-0 bg-gradient-to-r from-volexx-blue-500 to-volexx-blue-600 px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-white flex items-center space-x-2">
            <SparklesIcon className="h-6 w-6" />
            <span>Review Order</span>
          </h2>
          <button onClick={onClose} className="text-white hover:bg-white/20 p-2 rounded-volexx transition-colors">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div>
            <h3 className="font-bold text-gray-900 mb-3 flex items-center space-x-2">
              <MapPinIcon className="h-5 w-5 text-volexx-blue-500" />
              <span>Pickup Locations ({pickups.length})</span>
            </h3>
            <div className="space-y-2">
              {pickups.map((stop, idx) => (
                <div key={stop.id} className="bg-volexx-blue-50 border border-volexx-blue-200 rounded-volexx p-3">
                  <p className="font-semibold text-volexx-blue-900">#{idx + 1}. {stop.location?.address}</p>
                  <p className="text-sm text-gray-600">Contact: {stop.contactName} - {stop.contactPhone}</p>
                  {stop.timeWindow && <p className="text-xs text-gray-500">Time: {stop.timeWindow}</p>}
                </div>
              ))}
            </div>
          </div>

          <div>
            <h3 className="font-bold text-gray-900 mb-3 flex items-center space-x-2">
              <TruckIcon className="h-5 w-5 text-orange-500" />
              <span>Drop-off Locations ({dropoffs.length})</span>
            </h3>
            <div className="space-y-2">
              {dropoffs.map((stop, idx) => (
                <div key={stop.id} className="bg-orange-50 border border-orange-200 rounded-volexx p-3">
                  <p className="font-semibold text-orange-900">#{idx + 1}. {stop.location?.address}</p>
                  <p className="text-sm text-gray-600">Contact: {stop.contactName} - {stop.contactPhone}</p>
                  {stop.timeWindow && <p className="text-xs text-gray-500">Time: {stop.timeWindow}</p>}
                </div>
              ))}
            </div>
          </div>

          <div className="bg-gray-50 rounded-volexx-lg p-4">
            <h3 className="font-bold text-gray-900 mb-3">Package Details</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-gray-600">Type:</span>
                <p className="font-semibold">{formData.package_type.replace('_', ' ')}</p>
              </div>
              <div>
                <span className="text-gray-600">Weight:</span>
                <p className="font-semibold">{formData.package_weight} kg</p>
              </div>
              <div className="col-span-2">
                <span className="text-gray-600">Vehicle:</span>
                <p className="font-semibold">{formData.vehicle_type.replace('_', ' ')}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4">
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="flex-1 bg-gray-200 text-gray-700 py-3 px-6 rounded-volexx-lg font-semibold hover:bg-gray-300 transition-colors"
            >
              Back to Edit
            </button>
            <button
              onClick={onConfirm}
              className="flex-1 bg-gradient-to-r from-orange-500 to-orange-600 text-white py-3 px-6 rounded-volexx-lg font-bold hover:shadow-volexx-xl transform hover:scale-105 transition-all"
            >
              Proceed to Payment
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const PaymentSelectionModal: React.FC<{
  breakdown: PricingBreakdown;
  weight: string;
  walletBalance: number;
  pricingError?: string;
  onConfirm: (paymentMethod: 'wallet' | 'card', paymentGateway?: 'paystack' | 'seerbit') => void;
  onBack: () => void;
}> = ({ breakdown, weight, walletBalance, pricingError, onConfirm, onBack }) => {
  const [paymentMethod, setPaymentMethod] = useState<'wallet' | 'card'>('wallet');
  const [paymentGateway, setPaymentGateway] = useState<'paystack' | 'seerbit'>('paystack');
  
  const formatPrice = (amount: number): string => {
    return `₦${amount.toLocaleString()}`;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 animate-fadeIn">
      <div className="bg-white rounded-volexx-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-scaleIn">
        <div className="sticky top-0 bg-gradient-to-r from-volexx-blue-500 to-volexx-blue-600 px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-white flex items-center space-x-2">
            <CurrencyDollarIcon className="h-6 w-6" />
            <span>Pricing & Payment</span>
          </h2>
        </div>

        <div className="p-6 space-y-6">
          {breakdown.distance && breakdown.distance > 0 ? (
            <>
              <div className="bg-volexx-blue-50 rounded-volexx p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-700 font-medium">📍 Total Distance</span>
                  <span className="font-bold text-volexx-blue-700">{breakdown.distance.toFixed(2)} km</span>
                </div>
                {breakdown.totalDuration && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-700 font-medium">⏱️ Estimated Time</span>
                    <span className="font-bold text-volexx-blue-700">{breakdown.totalDuration.toFixed(0)} min</span>
                  </div>
                )}
              </div>

              <div className="bg-white border-2 border-volexx-blue-200 rounded-volexx-lg p-6">
                <div className="flex justify-between items-center">
                  <span className="text-xl font-bold text-gray-900">Estimated Total Cost</span>
                  <span className="text-4xl font-bold text-volexx-blue-600">₦{breakdown.total.toLocaleString()}</span>
                </div>
              </div>

              {pricingError ? (
                <div className="bg-orange-50 border border-orange-200 rounded-volexx p-3 text-sm text-orange-800">
                  ⚠️ {pricingError}
                </div>
              ) : (
                <div className="bg-green-50 border border-green-200 rounded-volexx p-3 text-sm text-green-800">
                  ✅ Real-time pricing calculated using Google Maps route data
                </div>
              )}
            </>
          ) : (
            <div className="bg-white border-2 border-volexx-blue-200 rounded-volexx-lg p-6">
              <div className="flex justify-between items-center">
                <span className="text-xl font-bold text-gray-900">Estimated Total Cost</span>
                <span className="text-4xl font-bold text-volexx-blue-600">₦{breakdown.total.toLocaleString()}</span>
              </div>
            </div>
          )}

          <div className="bg-gray-50 rounded-volexx-lg p-4">
            <h3 className="font-bold text-gray-900 mb-4">Payment Method</h3>
            <div className="space-y-3">
              <label className={`flex items-center p-4 border-2 rounded-volexx cursor-pointer transition-all ${
                paymentMethod === 'wallet' 
                  ? 'border-volexx-blue-500 bg-volexx-blue-50' 
                  : 'border-gray-300 hover:border-volexx-blue-300'
              }`}>
                <input
                  type="radio"
                  name="payment"
                  value="wallet"
                  checked={paymentMethod === 'wallet'}
                  onChange={() => setPaymentMethod('wallet')}
                  className="h-4 w-4 text-volexx-blue-600"
                />
                <div className="ml-3 flex-1">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-gray-900">Pay from Wallet</span>
                    <span className="text-sm font-bold text-volexx-blue-600">{formatPrice(walletBalance)}</span>
                  </div>
                  <p className="text-xs text-gray-600 mt-1">Current balance: {formatPrice(walletBalance)}</p>
                </div>
              </label>
              
              <label className={`flex items-center p-4 border-2 rounded-volexx cursor-pointer transition-all ${
                paymentMethod === 'card' 
                  ? 'border-volexx-blue-500 bg-volexx-blue-50' 
                  : 'border-gray-300 hover:border-volexx-blue-300'
              }`}>
                <input
                  type="radio"
                  name="payment"
                  value="card"
                  checked={paymentMethod === 'card'}
                  onChange={() => setPaymentMethod('card')}
                  className="h-4 w-4 text-volexx-blue-600"
                />
                <div className="ml-3">
                  <span className="font-semibold text-gray-900">Pay with Card</span>
                  <p className="text-xs text-gray-600">Card, Bank Transfer, USSD, Mobile Money</p>
                </div>
              </label>
            </div>
            
            {paymentMethod === 'card' && (
              <div className="mt-4 p-4 bg-white rounded-volexx border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-3">Select Payment Provider</h4>
                <div className="grid grid-cols-2 gap-3">
                  <label className={`flex flex-col items-center p-3 border-2 rounded-volexx cursor-pointer transition-all ${
                    paymentGateway === 'paystack' 
                      ? 'border-green-500 bg-green-50' 
                      : 'border-gray-300 hover:border-green-300'
                  }`}>
                    <input
                      type="radio"
                      name="gateway"
                      value="paystack"
                      checked={paymentGateway === 'paystack'}
                      onChange={() => setPaymentGateway('paystack')}
                      className="sr-only"
                    />
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-2">
                      <span className="text-2xl font-bold text-green-600">P</span>
                    </div>
                    <span className="font-semibold text-gray-900 text-sm">Paystack</span>
                    <p className="text-xs text-gray-500 text-center mt-1">Card, Bank, USSD</p>
                  </label>
                  
                  <label className={`flex flex-col items-center p-3 border-2 rounded-volexx cursor-pointer transition-all ${
                    paymentGateway === 'seerbit' 
                      ? 'border-purple-500 bg-purple-50' 
                      : 'border-gray-300 hover:border-purple-300'
                  }`}>
                    <input
                      type="radio"
                      name="gateway"
                      value="seerbit"
                      checked={paymentGateway === 'seerbit'}
                      onChange={() => setPaymentGateway('seerbit')}
                      className="sr-only"
                    />
                    <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-2">
                      <span className="text-2xl font-bold text-purple-600">S</span>
                    </div>
                    <span className="font-semibold text-gray-900 text-sm">SeerBit</span>
                    <p className="text-xs text-gray-500 text-center mt-1">Card, Transfer, USSD, Momo</p>
                  </label>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex space-x-3">
          <button
            onClick={onBack}
            className="flex-1 bg-gray-200 text-gray-700 py-3 px-6 rounded-volexx-lg font-semibold hover:bg-gray-300 transition-colors"
          >
            Back to Review
          </button>
          <button
            onClick={() => onConfirm(paymentMethod, paymentMethod === 'card' ? paymentGateway : undefined)}
            className="flex-1 bg-gradient-to-r from-orange-500 to-orange-600 text-white py-3 px-6 rounded-volexx-lg font-bold hover:shadow-volexx-xl transform hover:scale-105 transition-all"
          >
            Confirm & Create Order
          </button>
        </div>
      </div>
    </div>
  );
};

const CreateOrder: React.FC = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [stops, setStops] = useState<Stop[]>([
    {
      id: 'pickup-1',
      type: 'pickup',
      location: null,
      contactName: '',
      contactPhone: '',
      contactEmail: '',
      instructions: '',
      specialHandling: [],
    },
    {
      id: 'dropoff-1',
      type: 'dropoff',
      location: null,
      contactName: '',
      contactPhone: '',
      contactEmail: '',
      instructions: '',
      specialHandling: [],
    },
  ]);

  const [formData, setFormData] = useState({
    package_type: 'small_package',
    package_weight: '',
    vehicle_type: '',
    special_instructions: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pricingError, setPricingError] = useState('');
  const [showSummary, setShowSummary] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [walletBalance, setWalletBalance] = useState(0);
  const [pricingBreakdown, setPricingBreakdown] = useState<PricingBreakdown>({
    baseFare: 0,
    distanceFee: 0,
    weightFee: 0,
    stopsFee: 0,
    total: 0,
  });
  const [availableVehicleTypes, setAvailableVehicleTypes] = useState<Array<{
    vehicle_type: string;
    icon: string | null;
  }>>([]);
  const [vehicleTypesLoading, setVehicleTypesLoading] = useState(true);
  const [vehicleTypesError, setVehicleTypesError] = useState('');

  useEffect(() => {
    const fetchWallet = async () => {
      try {
        console.log('Fetching wallet balance...');
        const walletData = await walletAPI.getWallet();
        console.log('Wallet data received:', walletData);
        setWalletBalance(walletData.balance || 0);
      } catch (err) {
        console.error('Failed to fetch wallet balance:', err);
        setWalletBalance(0);
      }
    };
    fetchWallet();
  }, []);

  useEffect(() => {
    const fetchVehicleTypes = async () => {
      setVehicleTypesLoading(true);
      setVehicleTypesError('');
      try {
        const response = await pricingAPI.getVehicleTypes();
        console.log('Vehicle types response:', response);
        
        // Handle both possible response structures
        const configs = response.pricing_configs || response || [];
        
        if (!Array.isArray(configs) || configs.length === 0) {
          setVehicleTypesError('No vehicle types configured. Please contact administrator.');
          setAvailableVehicleTypes([]);
          setVehicleTypesLoading(false);
          return;
        }

        const vehicleTypes = configs.map((config: any) => ({
          vehicle_type: config.vehicle_type,
          icon: config.icon || null,
        }));
        setAvailableVehicleTypes(vehicleTypes);
        
        // Set vehicle type to first available
        setFormData(prev => ({ 
          ...prev, 
          vehicle_type: vehicleTypes[0].vehicle_type 
        }));
        setVehicleTypesLoading(false);
      } catch (err: any) {
        console.error('Failed to fetch vehicle types:', err);
        setVehicleTypesError('Unable to load vehicle types. Please try again later.');
        setAvailableVehicleTypes([]);
        setVehicleTypesLoading(false);
      }
    };
    fetchVehicleTypes();
  }, []);

  useEffect(() => {
    calculatePricing();
  }, [stops, formData.package_weight, formData.vehicle_type]);

  useEffect(() => {
    const allStopsHaveLocations = stops.every(s => s.location);
    const hasPackageDetails = formData.package_weight && parseFloat(formData.package_weight) > 0;
    
    if (showSummary) {
      setCurrentStep(3);
    } else if (allStopsHaveLocations && hasPackageDetails) {
      setCurrentStep(2);
    } else {
      setCurrentStep(1);
    }
  }, [stops, formData.package_weight, showSummary]);

  const calculatePricing = async () => {
    try {
      const validStops = stops.filter((s) => s.location);
      const weightNum = parseFloat(formData.package_weight) || 0;
      
      if (validStops.length < 2) {
        setPricingBreakdown({
          baseFare: 0,
          distanceFee: 0,
          weightFee: 0,
          stopsFee: 0,
          total: 0,
          distance: 0,
        });
        return;
      }

      const stopsData = validStops.map(stop => ({
        address: stop.location!.address,
        lat: stop.location!.lat,
        lng: stop.location!.lng,
        type: stop.type,
      }));

      const pricing = await pricingAPI.calculateRoutePricing({
        stops: stopsData,
        vehicle_type: formData.vehicle_type,
        package_weight_kg: weightNum,
      });

      setPricingError('');
      setPricingBreakdown({
        baseFare: pricing.base_fare,
        distanceFee: pricing.distance_fee,
        durationFee: pricing.duration_fee || 0,
        weightFee: pricing.weight_fee,
        stopsFee: 0,
        total: pricing.total,
        distance: pricing.total_distance_km || 0,
        vatAmount: pricing.vat_amount || 0,
        managementFee: pricing.management_fee || 0,
        paystackFee: pricing.paystack_fee || 0,
        subtotal: pricing.subtotal || 0,
        totalDuration: pricing.total_duration_minutes || 0,
        segments: pricing.segments || [],
      });
    } catch (error: any) {
      console.error('Error calculating pricing:', error);
      setPricingError('Unable to calculate real-time pricing.');
      setPricingBreakdown({
        baseFare: 0,
        distanceFee: 0,
        weightFee: 0,
        stopsFee: 0,
        total: 0,
        distance: 0,
      });
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const addStop = (type: 'pickup' | 'dropoff') => {
    const newStop: Stop = {
      id: `${type}-${Date.now()}`,
      type,
      location: null,
      contactName: '',
      contactPhone: '',
      contactEmail: '',
      instructions: '',
      specialHandling: [],
    };
    setStops([...stops, newStop]);
  };

  const removeStop = (id: string) => {
    setStops(stops.filter((stop) => stop.id !== id));
  };

  const updateStop = (id: string, field: string, value: any) => {
    setStops(
      stops.map((stop) =>
        stop.id === id ? { ...stop, [field]: value } : stop
      )
    );
  };

  const validateForm = (): boolean => {
    const pickups = stops.filter((s) => s.type === 'pickup');
    const dropoffs = stops.filter((s) => s.type === 'dropoff');

    if (pickups.length === 0 || dropoffs.length === 0) {
      setError('Please add at least one pickup and one dropoff location');
      return false;
    }

    for (const stop of stops) {
      if (!stop.location) {
        setError(`Please select a location for all stops`);
        return false;
      }
      if (!stop.contactName.trim()) {
        setError(`Please provide contact name for all stops`);
        return false;
      }
      if (!stop.contactPhone.trim()) {
        setError(`Please provide contact phone for all stops`);
        return false;
      }
    }

    if (!formData.package_weight || parseFloat(formData.package_weight) <= 0) {
      setError('Please enter a valid package weight');
      return false;
    }

    return true;
  };

  const handleReviewOrder = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    setCurrentStep(3);
    setShowSummary(true);
  };

  const handleSubmit = async (paymentMethod: 'wallet' | 'card', paymentGateway?: 'paystack' | 'seerbit') => {
    setLoading(true);
    setError('');

    try {
      const orderData = {
        package_type: formData.package_type,
        package_weight_kg: parseFloat(formData.package_weight),
        vehicle_type: formData.vehicle_type,
        special_instructions: formData.special_instructions,
        payment_method: paymentMethod,
        stops: stops.map(stop => ({
          address: stop.location!.address,
          lat: stop.location!.lat,
          lng: stop.location!.lng,
          type: stop.type,
          contact_name: stop.contactName,
          contact_phone: stop.contactPhone,
          contact_email: stop.contactEmail,
          instructions: stop.instructions || '',
        })),
      };

      if (paymentMethod === 'card') {
        const gateway = paymentGateway || 'paystack';
        let response;
        
        if (gateway === 'seerbit') {
          response = await ordersAPI.initializePaymentSeerBit(orderData);
        } else {
          response = await ordersAPI.initializePayment(orderData);
        }
        
        if (response.status === 'success' && response.authorization_url) {
          window.location.href = response.authorization_url;
          return;
        } else {
          throw new Error('Failed to initialize payment');
        }
      } else {
        const response = await ordersAPI.createOrder(orderData);
        setShowPayment(false);
        setCurrentStep(1);
        navigate(`/orders/${response.uuid}`);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to create order');
      setShowPayment(false);
    } finally {
      setLoading(false);
    }
  };

  const pickups = stops.filter((s) => s.type === 'pickup');
  const dropoffs = stops.filter((s) => s.type === 'dropoff');
  const canRemovePickup = pickups.length > 1;
  const canRemoveDropoff = dropoffs.length > 1;

  const [wizardStep, setWizardStep] = useState(1);
  
  const allLocationsHaveAddresses = stops.every(s => s.location);
  
  const handleNextStep = () => {
    if (wizardStep === 1) {
      if (!allLocationsHaveAddresses) {
        setError('Please select addresses for all pickup and dropoff locations before continuing.');
        return;
      }
      setError('');
      setWizardStep(2);
      calculatePricing();
    }
  };

  const handleBackStep = () => {
    if (wizardStep === 2) {
      setWizardStep(1);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8 animate-fadeInDown">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Create New Order</h1>
          <p className="text-gray-600">Let's get your package delivered! 🚀</p>
        </div>

        <div className="mb-8 animate-fadeInDown">
          <div className="flex items-center justify-center space-x-4">
            <div className={`flex items-center space-x-2 ${wizardStep >= 1 ? 'text-volexx-blue-600' : 'text-gray-400'}`}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${wizardStep >= 1 ? 'bg-volexx-blue-500 text-white' : 'bg-gray-200'}`}>
                1
              </div>
              <span className="font-semibold hidden sm:inline">Locations</span>
            </div>
            <div className={`h-1 w-16 ${wizardStep >= 2 ? 'bg-orange-500' : 'bg-gray-200'}`}></div>
            <div className={`flex items-center space-x-2 ${wizardStep >= 2 ? 'text-orange-600' : 'text-gray-400'}`}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${wizardStep >= 2 ? 'bg-orange-500 text-white' : 'bg-gray-200'}`}>
                2
              </div>
              <span className="font-semibold hidden sm:inline">Details & Pricing</span>
            </div>
          </div>
        </div>

        {wizardStep === 1 && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div className="bg-white rounded-volexx-xl shadow-volexx-xl border border-gray-200 p-8">
                <div className="space-y-8">
                  {error && (
                    <div className="bg-red-50 border-l-4 border-red-500 text-red-700 px-6 py-4 rounded-volexx-lg text-sm flex items-start space-x-3 animate-shake">
                      <ExclamationTriangleIcon className="w-5 h-5 flex-shrink-0 mt-0.5" />
                      <span>{error}</span>
                    </div>
                  )}

                  <div>
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-2xl font-bold text-gray-900 flex items-center space-x-2">
                        <MapPinIcon className="h-6 w-6 text-volexx-blue-500" />
                        <span>Delivery Route</span>
                      </h2>
                    </div>

                    {stops.filter(s => s.type === 'pickup').map((stop, index, pickups) => (
                      <React.Fragment key={stop.id}>
                        <StopCard
                          stop={stop}
                          index={index}
                          onUpdate={updateStop}
                          onRemove={removeStop}
                          canRemove={canRemovePickup}
                        />
                        {index < pickups.length - 1 && (
                          <div className="flex justify-center -my-2 relative z-10">
                            <div className="bg-gray-200 rounded-full p-2">
                              <ArrowDownIcon className="h-4 w-4 text-gray-500" />
                            </div>
                          </div>
                        )}
                      </React.Fragment>
                    ))}
                    
                    <div className="mt-6 mb-8">
                      <button
                        type="button"
                        onClick={() => addStop('pickup')}
                        className="w-full bg-gradient-blue text-white py-3 px-6 rounded-volexx-lg font-semibold hover:shadow-volexx-lg transform hover:scale-105 transition-all duration-300 flex items-center justify-center space-x-2"
                      >
                        <PlusIcon className="h-5 w-5" />
                        <span>Add Pickup Location</span>
                      </button>
                    </div>

                    {stops.filter(s => s.type === 'dropoff').map((stop, index, dropoffs) => (
                      <React.Fragment key={stop.id}>
                        <StopCard
                          stop={stop}
                          index={index}
                          onUpdate={updateStop}
                          onRemove={removeStop}
                          canRemove={canRemoveDropoff}
                        />
                        {index < dropoffs.length - 1 && (
                          <div className="flex justify-center -my-2 relative z-10">
                            <div className="bg-gray-200 rounded-full p-2">
                              <ArrowDownIcon className="h-4 w-4 text-gray-500" />
                            </div>
                          </div>
                        )}
                      </React.Fragment>
                    ))}
                    
                    <div className="mt-6">
                      <button
                        type="button"
                        onClick={() => addStop('dropoff')}
                        className="w-full bg-gradient-orange text-white py-3 px-6 rounded-volexx-lg font-semibold hover:shadow-volexx-lg transform hover:scale-105 transition-all duration-300 flex items-center justify-center space-x-2"
                      >
                        <PlusIcon className="h-5 w-5" />
                        <span>Add Drop-off Location</span>
                      </button>
                    </div>
                  </div>

                  <div className="flex space-x-4 pt-6 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={handleNextStep}
                      disabled={!allLocationsHaveAddresses}
                      className="flex-1 bg-gradient-to-r from-volexx-blue-500 to-volexx-blue-600 text-white py-4 px-8 rounded-volexx-lg font-bold text-lg hover:shadow-volexx-xl transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center space-x-2"
                    >
                      <span>Continue to Package Details</span>
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-1 space-y-6">
              <RouteMap stops={stops} />
              <div className="bg-volexx-blue-50 border border-volexx-blue-200 rounded-volexx-xl p-4">
                <div className="flex items-start space-x-3">
                  <MapPinIcon className="h-5 w-5 text-volexx-blue-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-volexx-blue-900">Can't find your address?</p>
                    <p className="text-xs text-volexx-blue-700 mt-1">
                      Click "Use Map" next to any address field to pin your exact location on the map.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {wizardStep === 2 && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div className="bg-white rounded-volexx-xl shadow-volexx-xl border border-gray-200 p-8">
                <form onSubmit={handleReviewOrder} className="space-y-8">
                  {error && (
                    <div className="bg-red-50 border-l-4 border-red-500 text-red-700 px-6 py-4 rounded-volexx-lg text-sm flex items-start space-x-3 animate-shake">
                      <ExclamationTriangleIcon className="w-5 h-5 flex-shrink-0 mt-0.5" />
                      <span>{error}</span>
                    </div>
                  )}

                  <div className="bg-volexx-blue-50 rounded-volexx-lg p-4 border border-volexx-blue-200">
                    <h3 className="font-semibold text-volexx-blue-900 mb-3 flex items-center space-x-2">
                      <CheckCircleIcon className="h-5 w-5 text-green-500" />
                      <span>Route Summary</span>
                    </h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-600">Pickups</p>
                        <p className="font-semibold text-volexx-blue-700">{pickups.length} location{pickups.length > 1 ? 's' : ''}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Drop-offs</p>
                        <p className="font-semibold text-orange-600">{dropoffs.length} location{dropoffs.length > 1 ? 's' : ''}</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleBackStep}
                      className="mt-3 text-sm text-volexx-blue-600 hover:text-volexx-blue-800 font-medium flex items-center space-x-1"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                      <span>Edit Locations</span>
                    </button>
                  </div>

                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center space-x-2">
                      <TruckIcon className="h-6 w-6 text-orange-500" />
                      <span>Package Details</span>
                    </h2>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="vehicle_type" className="block text-sm font-medium text-gray-700 mb-2">
                          Vehicle Type Required *
                        </label>
                        <select
                          id="vehicle_type"
                          name="vehicle_type"
                          required
                          className={`form-input ${vehicleTypesError ? 'border-red-300 bg-red-50' : ''}`}
                          value={formData.vehicle_type}
                          onChange={handleChange}
                          disabled={vehicleTypesLoading || availableVehicleTypes.length === 0}
                        >
                          {vehicleTypesLoading ? (
                            <option value="">Loading vehicle types...</option>
                          ) : availableVehicleTypes.length === 0 ? (
                            <option value="">No vehicle types available</option>
                          ) : (
                            availableVehicleTypes.map((vt) => (
                              <option key={vt.vehicle_type} value={vt.vehicle_type}>
                                {vt.icon ? `${vt.icon} ` : ''}
                                {vt.vehicle_type.split('_').map(word => 
                                  word.charAt(0).toUpperCase() + word.slice(1)
                                ).join(' ')}
                              </option>
                            ))
                          )}
                        </select>
                        {vehicleTypesError && (
                          <p className="mt-1 text-xs text-red-600 flex items-center">
                            <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                            {vehicleTypesError}
                          </p>
                        )}
                        {!vehicleTypesError && vehicleTypesLoading && (
                          <p className="mt-1 text-xs text-gray-500">
                            Fetching available vehicle types...
                          </p>
                        )}
                      </div>

                      <div>
                        <label htmlFor="package_weight" className="block text-sm font-medium text-gray-700 mb-2">
                          Package Weight (kg) *
                        </label>
                        <input
                          type="number"
                          step="0.1"
                          min="0.1"
                          id="package_weight"
                          name="package_weight"
                          required
                          className="form-input"
                          placeholder="e.g., 2.5"
                          value={formData.package_weight}
                          onChange={handleChange}
                        />
                        {formData.package_weight && parseFloat(formData.package_weight) > 5 && (
                          <p className="mt-2 text-sm text-orange-600 flex items-center space-x-1">
                            <ExclamationTriangleIcon className="h-4 w-4" />
                            <span>
                              Additional weight charges apply for packages over 5kg
                            </span>
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="mt-6">
                      <label htmlFor="special_instructions" className="block text-sm font-medium text-gray-700 mb-2">
                        General Special Instructions (Optional)
                      </label>
                      <textarea
                        id="special_instructions"
                        name="special_instructions"
                        rows={3}
                        className="form-input"
                        placeholder="Any general handling instructions for the entire order..."
                        value={formData.special_instructions}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  <div className="flex space-x-4 pt-6 border-t border-gray-200 sticky bottom-0 bg-white pb-4 -mb-4">
                    <button
                      type="button"
                      onClick={handleBackStep}
                      className="px-6 py-4 bg-gray-200 text-gray-700 rounded-volexx-lg font-semibold hover:bg-gray-300 transition-colors flex items-center space-x-2"
                    >
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                      <span>Back</span>
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex-1 bg-gradient-to-r from-orange-500 to-orange-600 text-white py-4 px-8 rounded-volexx-lg font-bold text-lg hover:shadow-volexx-xl transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center space-x-2"
                    >
                      <SparklesIcon className="h-6 w-6" />
                      <span>Review Order</span>
                    </button>
                  </div>
                </form>
              </div>
            </div>

            <div className="lg:col-span-1 space-y-6">
              <PricingPreview breakdown={pricingBreakdown} weight={formData.package_weight} pricingError={pricingError} showDetailed={false} />
              <RouteMap stops={stops} />
            </div>
          </div>
        )}
      </div>

      {showSummary && (
        <OrderSummaryModal
          stops={stops}
          formData={formData}
          onConfirm={() => {
            setShowSummary(false);
            setShowPayment(true);
          }}
          onClose={() => setShowSummary(false)}
        />
      )}

      {showPayment && (
        <PaymentSelectionModal
          breakdown={pricingBreakdown}
          weight={formData.package_weight}
          walletBalance={walletBalance}
          pricingError={pricingError}
          onConfirm={(paymentMethod, paymentGateway) => handleSubmit(paymentMethod, paymentGateway)}
          onBack={() => {
            setShowPayment(false);
            setShowSummary(true);
          }}
        />
      )}
    </div>
  );
};

export default CreateOrder;
